// React Query hooks for /api/*. Swaps in for the fixtures module —
// shape returned matches `shared/types.ts::Lead` exactly.
import { useQuery } from '@tanstack/react-query'
import type { LeadRow } from '@/shared/types'
import { config } from '@/config'

// Same-origin by default: the deployed web (nginx) container proxies /api/*
// to the API service, and the Vite dev server proxies it to :8000. config.apiUrl
// is the runtime-injected override (see ../config.ts).
const API_URL = config.apiUrl

// The session is an HttpOnly cookie set by the API after sign-in (see
// app/AuthGate.tsx) — it rides every same-origin request automatically and is
// never readable from JS, so there's nothing to store here. `credentials:
// 'include'` keeps it flowing even if the API is ever a different origin.
export class ApiError extends Error {
  status: number
  /** The parsed JSON error body, for a caller that reads more than `detail`. */
  body: unknown
  constructor(status: number, message: string, body?: unknown) {
    super(message)
    this.status = status
    this.body = body
  }
}

async function errorOf(res: Response): Promise<ApiError> {
  let body: unknown
  try {
    body = await res.json()
  } catch {
    // Non-JSON or empty error body — keep the status message.
  }
  return new ApiError(res.status, errorMessage(res.status, body), body)
}

/** Best-effort human-readable message out of a failed response's body.
 *
 * FastAPI puts it in `detail`, which has three shapes: a plain string for
 * `HTTPException`, an object carrying its own `detail` string when the
 * exception holds structured data, and a list of `{loc, msg, type}` objects
 * for a Pydantic validation 422. The list is flattened to "field: message" so
 * the rejected field is actually named — joining the objects directly would
 * render "[object Object]". Anything else (no body, non-JSON, unexpected
 * shape) falls back to the bare status.
 */
function errorMessage(status: number, body: unknown): string {
  const fallback = `Anropet misslyckades: ${status}`
  let detail = (body as { detail?: unknown } | null | undefined)?.detail
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    detail = (detail as { detail?: unknown }).detail
  }
  if (typeof detail === 'string' && detail.trim()) return detail.trim()
  if (Array.isArray(detail)) {
    const parts = detail
      .map((d) => {
        if (typeof d === 'string') return d.trim()
        if (typeof d?.msg !== 'string') return ''
        // `loc` is ['body', 'field', ...] — the envelope segment is noise.
        const loc = Array.isArray(d.loc)
          ? d.loc.filter((p: unknown) => p !== 'body' && p !== 'query').join('.')
          : ''
        return loc ? `${loc}: ${d.msg}` : d.msg
      })
      .filter(Boolean)
    if (parts.length) return parts.join('; ')
  }
  return fallback
}

// The last body each ETag-carrying GET returned, by URL. The lead reads answer
// a repeat request with 304 when nothing changed (app/api/shared/etag.py); the
// poll then resolves to the object it resolved to last time, so React Query
// sees identical data and the queue does not re-render or re-parse. Map order
// is insertion order, which makes the oldest entry the first key.
const MAX_REVALIDATED = 20
const revalidated = new Map<string, { etag: string; data: unknown }>()

function remember(url: string, etag: string, data: unknown): void {
  revalidated.delete(url)
  revalidated.set(url, { etag, data })
  if (revalidated.size > MAX_REVALIDATED) {
    revalidated.delete(revalidated.keys().next().value!)
  }
}

export async function apiFetch<T>(
  path: string,
  init: { method?: string; body?: unknown } = {},
): Promise<T> {
  const url = `${API_URL}${path}`
  const method = init.method || 'GET'
  const held = method === 'GET' ? revalidated.get(url) : undefined
  const headers: Record<string, string> = {}
  if (init.body) headers['Content-Type'] = 'application/json'
  // A request carrying If-None-Match bypasses the browser's HTTP cache, so the
  // 304 arrives here instead of being swapped for the cached 200.
  if (held) headers['If-None-Match'] = held.etag
  const res = await fetch(url, {
    credentials: 'include',
    method,
    headers,
    body: init.body ? JSON.stringify(init.body) : undefined,
  })
  if (res.status === 304 && held) {
    remember(url, held.etag, held.data)
    return held.data as T
  }
  if (res.status === 401) {
    // Not signed in / session expired. AuthGate sees the 401 and shows the
    // sign-in screen (the SPA shell stays mounted).
    throw new ApiError(401, 'Inte inloggad')
  }
  if (res.status === 403) {
    throw new ApiError(403, 'CRM-åtkomst saknas för det här kontot')
  }
  if (!res.ok) {
    throw await errorOf(res)
  }
  const data = await res.json()
  const etag = method === 'GET' ? res.headers.get('ETag') : null
  if (etag) remember(url, etag, data)
  else revalidated.delete(url)
  return data
}

/** POST multipart/form-data (file upload).
 *
 * Can't go through apiFetch: the browser has to set Content-Type itself so the
 * multipart boundary is right. Error handling matches apiFetch's — the
 * lead-list import returns actionable `detail` messages ("Not a readable .xlsx
 * file", "Missing permission: import_leads").
 */
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    credentials: 'include',
    method: 'POST',
    body: form,
  })
  if (res.status === 401) {
    throw new ApiError(401, 'Inte inloggad')
  }
  if (!res.ok) {
    throw await errorOf(res)
  }
  return res.json()
}

export interface AuthConfig {
  sso: boolean // Microsoft / Entra configured (the public YLLW sign-in)
  google: boolean // Google configured (the hidden Solvigo /admin/login sign-in)
  methods: string[]
}

export function useAuthConfig() {
  return useQuery({
    queryKey: ['auth-config'],
    queryFn: () => apiFetch<AuthConfig>('/auth/config'),
    staleTime: Infinity,
    retry: false,
  })
}

export interface LeadsResponse {
  leads: LeadRow[]
  total: number
  /** Whose queue this is. Echoed back so the UI can name it — an admin looking at
   *  a seller's queue must never be unsure whose leads are on screen. */
  queueOwner?: string | null
  /** Per-band counts over the seller's whole book — focus segment only. Lets
   *  the queue account for the leads it is NOT showing. */
  bandCounts?: Record<string, number> | null
}
